import { useState } from "react";

export default function NewsletterBanner() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus("loading");

    const body = new URLSearchParams({ email });

    try {
      const res = await fetch("https://readdy.ai/api/form/d725i4ohb0rqicn68sp0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus("success");
        setMessage("You&apos;re in! Expect your first insight next week.");
        setEmail("");
      } else {
        setStatus("error");
        setMessage("Something went wrong. Try again or email info@ltfsourcing.com");
      }
    } catch {
      setStatus("error");
      setMessage("Connection issue. Please try again.");
    }
  };

  return (
    <section className="py-16 bg-[#222831]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left */}
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#C9A227] mb-4">
              <i className="ri-mail-send-line text-sm"></i>
              Weekly Sourcing Intelligence
            </span>
            <h2 className="text-2xl lg:text-4xl font-extrabold text-white leading-tight">
              The Procurement Edge, <br />
              <span className="text-[#C9A227]">Every Tuesday Morning</span>
            </h2>
            <p className="mt-4 text-sm text-white/60 leading-relaxed max-w-md">
              One article, one insight, one actionable strategy from our procurement team — delivered to your inbox weekly. No filler, no sales pitches, no spam.
            </p>

            {/* Social proof */}
            <div className="mt-6 flex items-center gap-4">
              <div className="flex -space-x-2">
                {["bg-amber-700", "bg-[#3A506B]", "bg-[#C9A227]", "bg-teal-700"].map((c, i) => (
                  <div
                    key={i}
                    className={`w-8 h-8 flex items-center justify-center rounded-full ${c} border-2 border-[#222831] text-white text-xs font-bold`}
                  >
                    {["RT", "MC", "JW", "DK"][i]}
                  </div>
                ))}
              </div>
              <p className="text-xs text-white/50">
                Written by the LTF Sourcing team &mdash; 6 contributors
              </p>
            </div>
          </div>

          {/* Right */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            {status === "success" ? (
              <div className="flex flex-col items-center text-center py-6">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#C9A227]/20 mb-4">
                  <i className="ri-check-line text-2xl text-[#C9A227]"></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">You&apos;re subscribed!</h3>
                <p className="text-sm text-white/60">{message}</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-white mb-1">Subscribe for Free</h3>
                <p className="text-sm text-white/50 mb-6">Join professionals who get smarter about procurement every week.</p>

                <form
                  id="blog-newsletter-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-3"
                >
                  <div>
                    <label htmlFor="newsletter-email" className="text-xs text-white/60 font-semibold uppercase tracking-widest block mb-2">
                      Email Address
                    </label>
                    <input
                      id="newsletter-email"
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@company.com"
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C9A227] transition-colors"
                    />
                  </div>

                  {status === "error" && (
                    <p className="text-xs text-red-400">{message}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full py-3 rounded-lg bg-[#C9A227] text-[#222831] text-sm font-bold hover:bg-[#b8911f] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Subscribing…
                      </>
                    ) : (
                      <>
                        <i className="ri-mail-add-line"></i>
                        Subscribe — It&apos;s Free
                      </>
                    )}
                  </button>

                  <p className="text-xs text-white/30 text-center">
                    Unsubscribe any time. We respect your inbox.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
